import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, CircularProgress, Switch,
    FormControlLabel, IconButton, Tooltip, TextField, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { tokens } from '../../../../theme';
import v2Api from '../../../../utils/v2Api';
import getCookies from '../../../../utils/getCookies';
import Axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getAuthHeader } from '../../../../utils/authToken';
import RetakeResultSummary from './RetakeResultSummary';
import { sessionStatusColor } from './resultUtils';
import { scaleLabel, scaleOf, formatScaled } from './scoreScale';
dayjs.extend(utc);
dayjs.extend(timezone);

const TestResultV2 = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id: courseUuid, courseTestId } = useParams();
    const navigate = useNavigate();

    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const isAdmin = userRole === 'admin';

    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [courseTest, setCourseTest] = useState(null);
    const [releaseGrades, setReleaseGrades] = useState(false);
    const [testSections, setTestSections] = useState([]);
    const [enrolledStudents, setEnrolledStudents] = useState([]);
    const [expandedDisplayScore, setExpandedDisplayScore] = useState(null); // session id
    const [displayScoreDraft, setDisplayScoreDraft] = useState({}); // { sections: {}, total: '', ap_score: '' }

    const fetchData = async () => {
        try {
            const [sessionsRes, ctRes] = await Promise.all([
                v2Api.get(`/course-tests/${courseTestId}/sessions`),
                v2Api.get(`/course-tests/${courseTestId}`),
            ]);
            setSessions(sessionsRes.data.data || []);
            const ct = ctRes.data.data;
            setCourseTest(ct);
            setReleaseGrades(ct.v2_test?.release_grades ?? false);

            // Fetch test structure for section names
            if (ct.test_id) {
                const testRes = await v2Api.get(`/tests/${ct.test_id}`);
                setTestSections(testRes.data.data?.v2_test_sections || []);
            }

            // Fetch enrolled students for the course
            try {
                const studentsRes = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/courses/${courseUuid}/students`,
                    { headers: { Authorization: getAuthHeader() } }
                );
                setEnrolledStudents((studentsRes.data || []).map(sc => sc.student).filter(Boolean));
            } catch { /* ignore */ }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [courseTestId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll for grading progress when any session has pending FRQ
    const hasAnyPending = sessions.some(s =>
        (s.v2_student_part_scores || []).some(ps => ps.grading_status === 'pending' || ps.grading_status === 'grading')
    );
    useEffect(() => {
        if (!hasAnyPending) return;
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, [hasAnyPending, courseTestId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggleRelease = async () => {
        try {
            const testId = courseTest?.test_id;
            if (!testId) return;
            const res = await v2Api.patch(`/tests/${testId}/release-grades`);
            setReleaseGrades(res.data.release_grades);
        } catch (err) {
            console.error('Failed to toggle grade release:', err);
        }
    };

    const toggleDisplayScore = (session) => {
        if (expandedDisplayScore === session.id) {
            setExpandedDisplayScore(null);
        } else {
            setExpandedDisplayScore(session.id);
            const ds = session.display_scores || {};
            setDisplayScoreDraft({
                sections: ds.sections || {},
                total: ds.total ?? '',
                ap_score: ds.ap_score ?? '',
            });
        }
    };

    const saveDisplayScores = async (sessionId) => {
        try {
            const cleaned = {
                sections: Object.fromEntries(
                    Object.entries(displayScoreDraft.sections).filter(([_, v]) => v !== '' && v != null).map(([k, v]) => [k, parseFloat(v)])
                ),
                // parseFloat: an AMC display score can be 121.5; AP/ACT whole numbers parse the same.
                ap_score: displayScoreDraft.ap_score !== '' ? parseFloat(displayScoreDraft.ap_score) : null,
            };
            const isEmpty = Object.keys(cleaned.sections).length === 0 && cleaned.ap_score === null;
            await v2Api.patch(`/sessions/${sessionId}/display-scores`, { display_scores: isEmpty ? null : cleaned });
            setExpandedDisplayScore(null);
            fetchData();
        } catch (err) {
            console.error('Failed to save display scores:', err);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;

    const test = courseTest?.v2_test;
    // Any scoring profile yields a scaled score — AP 1-5, ACT 1-36, … — so this is not AP-specific.
    const hasScaledScore = !!test?.scoring_profile_id;

    // Build combined list: sessions + students who haven't started
    const sessionStudentIds = new Set(sessions.map(s => s.student_id));
    const assignedStudentIds = (courseTest?.v2_course_test_students || []).map(s => s.student_id);
    const allAssigned = assignedStudentIds.length > 0; // empty = all enrolled
    const notStartedStudents = enrolledStudents.filter(student => {
        if (!allAssigned && !sessionStudentIds.has(student.id)) return true;
        if (allAssigned && assignedStudentIds.includes(student.id) && !sessionStudentIds.has(student.id)) return true;
        return false;
    });

    // Retakeable tests: show the per-student attempt summary (unlimited attempts,
    // best/latest/avg + drill-in) instead of the one-row-per-session table.
    if (test?.allow_retake) {
        return (
            <RetakeResultSummary
                courseUuid={courseUuid}
                courseTestId={courseTestId}
                courseTest={courseTest}
                test={test}
                sessions={sessions}
                notStartedStudents={notStartedStudents}
                isAdmin={isAdmin}
                releaseGrades={releaseGrades}
                onToggleRelease={handleToggleRelease}
                navigate={navigate}
            />
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate(`/classrooms/${courseUuid}/tests`)}><ArrowBackIcon /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={600}>
                        {courseTest?.title || test?.title} — Submissions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {sessions.length} submitted, {notStartedStudents.length} not started
                    </Typography>
                </Box>
                {isAdmin && <>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}?preview=true`)}
                        sx={{ borderColor: colors.blueAccent[600], color: colors.blueAccent[600], ':hover': { borderColor: colors.blueAccent[400], bgcolor: `${colors.blueAccent[600]}10` } }}
                    >
                        Preview
                    </Button>
                    <FormControlLabel
                        control={<Switch checked={releaseGrades} onChange={handleToggleRelease} />}
                        label="Release grades to students"
                    />
                </>}
            </Box>

            {/* Submissions table */}
            <TableContainer component={Paper} sx={{ bgcolor: colors.primary[600] }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Student</b></TableCell>
                            <TableCell align="center"><b>Student Submitted</b></TableCell>
                            <TableCell align="center"><b>Admin Submitted</b></TableCell>
                            <TableCell align="center"><b>Status</b></TableCell>
                            {testSections.map((sec, idx) => (
                                <TableCell key={idx} align="center"><b>{sec.section_name || `Section ${idx + 1}`}</b></TableCell>
                            ))}
                            {testSections.length > 1 && <TableCell align="center"><b>Total</b></TableCell>}
                            {hasScaledScore && <TableCell align="center"><b>{scaleLabel(test)}</b></TableCell>}
                            <TableCell align="center"><b>Flags</b></TableCell>
                            {isAdmin && <TableCell align="center"><b>Actions</b></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sessions.length === 0 && notStartedStudents.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3 + testSections.length + (testSections.length > 1 ? 1 : 0) + (hasScaledScore ? 1 : 0) + (isAdmin ? 1 : 0)} align="center">
                                    <Typography color="text.secondary" py={4}>No students assigned</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                            {sessions.map(s => {
                                const studentAnswers = s.v2_student_answers || [];
                                const partScores = s.v2_student_part_scores || [];
                                const answerMap = {};
                                studentAnswers.forEach(a => { answerMap[a.question_id] = a; });
                                const flagCount = studentAnswers.filter(a => a.flagged).length;
                                const ds = s.display_scores || {};

                                // Per-section scores
                                let grandScore = 0, grandTotal = 0;
                                const sectionCells = testSections.map((sec, idx) => {
                                    const qs = sec.v2_test_questions || [];
                                    let score = 0, total = 0, pending = false;
                                    const cgTotals = {}, cgScores = {};

                                    for (const q of qs) {
                                        const sa = answerMap[q.id];
                                        const isMcq = ['mcq', 'multi_answer', 'grid_in', 'sa'].includes(q.question_type);
                                        if (isMcq) {
                                            total += (q.max_points || 1);
                                            if (sa?.is_correct) score += (sa.points_earned || 1);
                                        } else {
                                            const questionParts = q.v2_question_parts || [];
                                            const leafParts = questionParts.filter(p => !questionParts.some(c => c.parent_id === p.id));
                                            let qTotal = 0, qScore = 0, qPending = false;
                                            for (const part of leafParts) {
                                                qTotal += (part.max_points || 0);
                                                const ps = partScores.find(ps => ps.part_id === part.id);
                                                if (ps) {
                                                    qScore += (ps.score || 0);
                                                    if (ps.grading_status === 'pending' || ps.grading_status === 'grading') qPending = true;
                                                }
                                            }
                                            if (leafParts.length === 0) {
                                                // Zero-part FRQ — score lives on the part_id=null row.
                                                qTotal = q.max_points || 0;
                                                const wps = partScores.find(ps => ps.question_id === q.id && ps.part_id == null);
                                                if (wps) {
                                                    qScore = wps.score || 0;
                                                    if (wps.grading_status === 'pending' || wps.grading_status === 'grading') qPending = true;
                                                }
                                            }
                                            if (q.choice_group) {
                                                cgTotals[q.choice_group] = Math.max(cgTotals[q.choice_group] || 0, qTotal);
                                                if (partScores.some(ps => ps.question_id === q.id)) cgScores[q.choice_group] = { score: qScore, pending: qPending };
                                            } else {
                                                total += qTotal;
                                                score += qScore;
                                                if (qPending) pending = true;
                                            }
                                        }
                                    }
                                    for (const g of Object.keys(cgTotals)) {
                                        total += cgTotals[g];
                                        if (cgScores[g]) { score += cgScores[g].score; if (cgScores[g].pending) pending = true; }
                                    }
                                    grandScore += score;
                                    grandTotal += total;

                                    if (s.status === 'in_progress') return <TableCell key={idx} align="center">—</TableCell>;
                                    if (pending) {
                                        // Count graded questions
                                        const qMap = {};
                                        partScores.filter(ps => qs.some(q => q.id === ps.question_id)).forEach(ps => {
                                            if (!qMap[ps.question_id]) qMap[ps.question_id] = { total: 0, done: 0 };
                                            qMap[ps.question_id].total++;
                                            if (ps.grading_status === 'completed' || ps.grading_status === 'manual') qMap[ps.question_id].done++;
                                        });
                                        const qVals = Object.values(qMap);
                                        return (
                                            <TableCell key={idx} align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                                    <CircularProgress size={14} />
                                                    <Typography variant="caption">{qVals.filter(q => q.done === q.total).length}/{qVals.length}</Typography>
                                                </Box>
                                            </TableCell>
                                        );
                                    }
                                    const displayScore = ds.sections?.[sec.id];
                                    return <TableCell key={idx} align="center">
                                        {displayScore != null ? (
                                            <Tooltip title={`Actual: ${score}/${total}`}><span><b>{displayScore}</b>/{total}</span></Tooltip>
                                        ) : `${score}/${total}`}
                                    </TableCell>;
                                });

                                const statusColor = sessionStatusColor(s.status);

                                return (
                                    <React.Fragment key={s.id}>
                                    <TableRow
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}/result/${s.id}`)}
                                    >
                                        <TableCell>
                                            {s.student
                                                ? `${s.student.first_name} ${s.student.last_name}${s.student.consultation_number ? ` (${s.student.consultation_number})` : ''}`
                                                : s.placement_test_taker
                                                    ? (s.placement_test_taker.name || s.placement_test_taker.email || `Placement #${s.placement_test_taker.id}`)
                                                    : `Student #${s.student_id}`}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                                            {s.end_time ? dayjs(s.end_time).tz('America/New_York').format('M/D/YY h:mma') : '—'}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                                            {s.admin_submitted_at ? dayjs(s.admin_submitted_at).tz('America/New_York').format('M/D/YY h:mma') : '—'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={s.status} size="small" color={statusColor} />
                                        </TableCell>
                                        {sectionCells}
                                        {testSections.length > 1 && (() => {
                                            // Compute display total from section display overrides
                                            let displayTotal = 0;
                                            let hasAnyOverride = false;
                                            testSections.forEach(sec => {
                                                if (ds.sections?.[sec.id] != null) {
                                                    displayTotal += ds.sections[sec.id];
                                                    hasAnyOverride = true;
                                                }
                                            });
                                            return (
                                            <TableCell align="center">
                                                {s.status === 'in_progress' ? '—' :
                                                    hasAnyOverride ? (
                                                        <Tooltip title={`Actual: ${grandScore}/${grandTotal}`}><span><b>{displayTotal}</b>/{grandTotal}</span></Tooltip>
                                                    ) : `${grandScore}/${grandTotal}`
                                                }
                                            </TableCell>
                                            );
                                        })()}
                                        {hasScaledScore && (
                                            <TableCell align="center">
                                                {/* "102/150" for AMC (the session's own max), bare number for AP / ACT. */}
                                                {ds.ap_score != null ? (
                                                    <Tooltip title={`Actual: ${formatScaled(scaleOf(test), s.scaled_score, s.section_scores?.AMC?.max) ?? '—'}`}>
                                                        <b>{formatScaled(scaleOf(test), ds.ap_score, s.section_scores?.AMC?.max)}</b>
                                                    </Tooltip>
                                                ) : s.scaled_score != null ? <b>{formatScaled(scaleOf(test), s.scaled_score, s.section_scores?.AMC?.max)}</b> : '—'}
                                            </TableCell>
                                        )}
                                        <TableCell align="center">
                                            {flagCount > 0 ? (
                                                <Chip label={flagCount} size="small" sx={{ bgcolor: '#f59e0b20', color: '#f59e0b' }} />
                                            ) : '—'}
                                        </TableCell>
                                        {isAdmin && <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="Edit on behalf">
                                                <IconButton size="small" onClick={() => navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}?student=${s.student_id}`)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {partScores.length > 0 && (
                                                <Tooltip title="Regrade immediately">
                                                    <IconButton size="small" onClick={async () => {
                                                        try { await v2Api.post(`/sessions/${s.id}/regrade`); fetchData(); }
                                                        catch (err) { console.error('Regrade failed:', err); }
                                                    }}><ReplayIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>}
                                    </TableRow>
                                    {/* Display score row — always visible for admins */}
                                    {isAdmin && (() => {
                                        const draft = expandedDisplayScore === s.id ? displayScoreDraft : null;
                                        const dsVal = (secId) => draft ? (draft.sections?.[secId] ?? '') : (ds.sections?.[secId] ?? '');
                                        const dsTotal = draft ? (draft.total ?? '') : (ds.total ?? '');
                                        const dsAp = draft ? (draft.ap_score ?? '') : (ds.ap_score ?? '');
                                        const isEditing = expandedDisplayScore === s.id;
                                        const startEdit = () => {
                                            setExpandedDisplayScore(s.id);
                                            setDisplayScoreDraft({
                                                sections: ds.sections || {},
                                                ap_score: ds.ap_score ?? '',
                                            });
                                        };
                                        // Check if draft differs from saved
                                        const isDirty = isEditing && (() => {
                                            const saved = ds || {};
                                            const savedSections = saved.sections || {};
                                            const draftSections = displayScoreDraft.sections || {};
                                            const allSecIds = new Set([...Object.keys(savedSections), ...Object.keys(draftSections)]);
                                            for (const id of allSecIds) {
                                                if (String(savedSections[id] ?? '') !== String(draftSections[id] ?? '')) return true;
                                            }
                                            if (String(saved.ap_score ?? '') !== String(displayScoreDraft.ap_score ?? '')) return true;
                                            return false;
                                        })();
                                        const cellSx = { bgcolor: 'action.hover', py: 0.5 };
                                        return (
                                        <TableRow onClick={(e) => { e.stopPropagation(); if (!isEditing) startEdit(); }}>
                                            <TableCell colSpan={4} sx={cellSx}>
                                                <Typography variant="caption" color="text.secondary">Display</Typography>
                                            </TableCell>
                                            {testSections.map((sec, idx) => (
                                                <TableCell key={idx} align="center" sx={cellSx}>
                                                    <TextField size="small" variant="standard" placeholder="—"
                                                        type="number"
                                                        value={dsVal(sec.id)}
                                                        onFocus={() => { if (!isEditing) startEdit(); }}
                                                        onChange={(e) => setDisplayScoreDraft(prev => ({
                                                            ...prev, sections: { ...prev.sections, [sec.id]: e.target.value },
                                                        }))}
                                                        sx={{ width: 50 }}
                                                        inputProps={{ style: { textAlign: 'center', fontSize: 13 } }}
                                                    />
                                                </TableCell>
                                            ))}
                                            {testSections.length > 1 && (
                                                <TableCell sx={cellSx} />
                                            )}
                                            {hasScaledScore && (
                                                <TableCell align="center" sx={cellSx}>
                                                    <TextField size="small" variant="standard" placeholder="—"
                                                        type="number" value={dsAp}
                                                        onFocus={() => { if (!isEditing) startEdit(); }}
                                                        onChange={(e) => setDisplayScoreDraft(prev => ({ ...prev, ap_score: e.target.value }))}
                                                        sx={{ width: 40 }}
                                                        inputProps={{ style: { textAlign: 'center', fontSize: 13 }, min: scaleOf(test).min, max: scaleOf(test).max, step: scaleOf(test).step ?? 1 }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell sx={cellSx}>{' '}</TableCell>
                                            {isAdmin && (
                                                <TableCell align="center" sx={cellSx}>
                                                    {isDirty && (
                                                        <Button size="small" variant="contained" onClick={() => saveDisplayScores(s.id)}
                                                            sx={{ fontSize: 11, textTransform: 'none' }}>
                                                            Save
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        );
                                    })()}
                                </React.Fragment>
                                );
                            })}
                            {/* Not started students */}
                            {notStartedStudents.map(student => (
                                <TableRow key={`ns-${student.id}`} sx={{ opacity: 0.6, cursor: isAdmin ? 'pointer' : 'default' }}
                                    hover={isAdmin}
                                    onClick={() => isAdmin && navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}?student=${student.id}`)}
                                >
                                    <TableCell>
                                        {`${student.first_name} ${student.last_name}${student.consultation_number ? ` (${student.consultation_number})` : ''}`}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                                        —
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                                        —
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label="not started" size="small" variant="outlined" />
                                    </TableCell>
                                    {testSections.map((_, idx) => <TableCell key={idx} align="center">—</TableCell>)}
                                    {testSections.length > 1 && <TableCell align="center">—</TableCell>}
                                    {hasScaledScore && <TableCell align="center">—</TableCell>}
                                    <TableCell align="center">—</TableCell>
                                    {isAdmin && <TableCell />}
                                </TableRow>
                            ))}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TestResultV2;
